// 跨窗口登录态桥：
// - sessionStorage 仍是「每窗口运行态」（业务读取零改动）
// - localStorage 仅作「新窗口启动恢复」用的单份会话快照
// 纯逻辑在 authSession.core.js（Node 单测）；本文件只做 window.* 副作用。
// 依赖方向：本文件只 import core，禁止 import menuRuntime / router（防循环依赖）。
import {
  pickSnapshotData,
  encodeSnapshot,
  decodeSnapshot,
  writeSnapshotData,
  clearAuthSessionData,
  SNAPSHOT_MAX_TTL_MS,
} from "./authSession.core.js";

const LS_SNAPSHOT_KEY = "__auth_session_snapshot__";
const LS_LOGOUT_SIGNAL = "__auth_logout_signal__";

let lastCapturedJson = null; // 进程内去重，避免每次导航都写 localStorage

function normalizeLoginPage(loginPage) {
  return String(loginPage || "").split("?")[0];
}

function currentLogoutScope(storage) {
  const s = storage || window.sessionStorage;
  return {
    accountScope: s.getItem("accountScope") || "",
    loginPage: normalizeLoginPage(s.getItem("loginPage") || ""),
    guardName: s.getItem("guardName") || "",
  };
}

function parseLogoutSignal(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && parsed.scope ? parsed.scope : null;
  } catch (e) {
    return null;
  }
}

function shouldAcceptLogoutSignal(signalScope) {
  if (!signalScope) {
    return true;
  }
  const current = currentLogoutScope(window.sessionStorage);
  if (signalScope.accountScope && current.accountScope) {
    return signalScope.accountScope === current.accountScope;
  }
  const signalLoginPage = normalizeLoginPage(signalScope.loginPage || "");
  if (signalLoginPage && current.loginPage) {
    return signalLoginPage === current.loginPage;
  }
  if (signalScope.guardName && current.guardName) {
    return signalScope.guardName === current.guardName;
  }
  return true;
}

// 当前窗口已登录 -> 把会话快照写入 localStorage（数据未变则跳过）
function captureSnapshot() {
  const data = pickSnapshotData(window.sessionStorage);
  if (!data) return;
  const dataJson = JSON.stringify(data);
  if (dataJson === lastCapturedJson) return; // 会话未变，不重复写
  lastCapturedJson = dataJson;
  window.localStorage.setItem(LS_SNAPSHOT_KEY, encodeSnapshot(data, Date.now()));
}

// 当前窗口未登录但有有效快照 -> 同步恢复进 sessionStorage。返回是否恢复。
function restoreSnapshotIfNeeded() {
  if (window.sessionStorage.getItem("token")) return false; // 本窗口已有会话
  const raw = window.localStorage.getItem(LS_SNAPSHOT_KEY);
  const data = decodeSnapshot(raw, Date.now(), SNAPSHOT_MAX_TTL_MS); // 过期主要看 token JWT exp；此值仅兜底
  if (!data) {
    if (raw) window.localStorage.removeItem(LS_SNAPSHOT_KEY); // 无效/过期顺手清掉
    return false;
  }
  writeSnapshotData(window.sessionStorage, data);
  lastCapturedJson = JSON.stringify(data); // 恢复即视为已捕获，避免随后回写抖动
  return true;
}

// 登出/禁用：清本地快照
function clearSnapshot() {
  lastCapturedJson = null;
  window.localStorage.removeItem(LS_SNAPSHOT_KEY);
}

// 登出/禁用：广播给其它同账号类型窗口，避免区域账号退出误踢普通店铺账号。
function broadcastLogout(scope) {
  window.localStorage.setItem(LS_LOGOUT_SIGNAL, JSON.stringify({
    time: Date.now(),
    scope: scope || currentLogoutScope(window.sessionStorage),
  }));
  window.localStorage.removeItem(LS_LOGOUT_SIGNAL);
}

// 其它窗口登出时，本窗口同步登出。
// 用整页 reload（mode 无关、状态最干净，且不调 menuRuntime/不再广播 -> 无回环）。
function installLogoutSync() {
  window.addEventListener("storage", (e) => {
    if (e.key === LS_LOGOUT_SIGNAL && e.newValue) {
      if (!shouldAcceptLogoutSignal(parseLogoutSignal(e.newValue))) {
        return;
      }
      // sessionStorage 是窗口私有，别的窗口清不到本窗口，这里整体清空本窗口会话。
      // 用 clear() 而非清部分键：避免残留 IMtoken/routes 与已清的 userinfo 不一致
      //（守卫 IM 分支会读 userinfo.roles 而抛错白屏）；登出本就该丢弃本窗口全部运行态。
      window.sessionStorage.clear();
      clearSnapshot();
      window.location.reload(); // 守卫将看到无 token + 无快照 -> 自动落登录页
    }
  });
}

export {
  captureSnapshot,
  restoreSnapshotIfNeeded,
  clearAuthSessionData,
  clearSnapshot,
  broadcastLogout,
  installLogoutSync,
};
export default {
  captureSnapshot,
  restoreSnapshotIfNeeded,
  clearAuthSessionData,
  clearSnapshot,
  broadcastLogout,
  installLogoutSync,
};
